/**
 * @fileoverview Premium EmptyState Component for Skillzy.pro
 *
 * Beautiful empty state placeholder with icon, title, description,
 * action button, and entrance animations.
 *
 * @example
 * import { BookOpen, Plus } from 'lucide-react';
 *
 * <EmptyState
 *   icon={<BookOpen />}
 *   title="No courses yet"
 *   description="Create your first course to get started with your learning journey."
 *   actionLabel="Create Course"
 *   actionIcon={<Plus />}
 *   onAction={() => navigate('/courses/new')}
 * />
 *
 * <EmptyState
 *   variant="minimal"
 *   icon={<Search />}
 *   title="No results found"
 *   description="Try adjusting your search or filter criteria."
 * />
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

/** @type {Record<string, string>} */
const variantClasses = {
  default: '',
  card: clsx(
    'bg-white border border-slate-200/80 rounded-2xl shadow-sm',
    'dark:bg-slate-900 dark:border-slate-800'
  ),
  dashed: clsx(
    'border-2 border-dashed border-slate-300 rounded-2xl',
    'dark:border-slate-700'
  ),
  minimal: '',
};

/** @type {Record<string, string>} */
const iconContainerClasses = {
  default: clsx(
    'bg-slate-100 text-slate-400',
    'dark:bg-slate-800 dark:text-slate-500'
  ),
  primary: clsx(
    'bg-indigo-50 text-indigo-500',
    'dark:bg-indigo-500/10 dark:text-indigo-400'
  ),
  gradient: clsx(
    'bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-500',
    'text-white shadow-lg shadow-indigo-500/20'
  ),
};

/** @type {Record<string, string>} */
const sizeClasses = {
  sm: 'py-8 px-6',
  md: 'py-12 px-8',
  lg: 'py-16 px-10',
};

const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemAnimation = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0, 0, 0.2, 1] },
  },
};

const iconFloatAnimation = {
  hidden: { opacity: 0, scale: 0.8, y: 8 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.175, 0.885, 0.32, 1.275], // Back ease for spring feel
    },
  },
};

/**
 * Premium EmptyState component for zero-data states.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.icon] - Lucide icon or custom illustration
 * @param {string} props.title - Main heading
 * @param {string} [props.description] - Supporting description text
 * @param {string} [props.actionLabel] - Primary CTA button text
 * @param {React.ReactNode} [props.actionIcon] - Icon for the primary CTA
 * @param {Function} [props.onAction] - Primary CTA click handler
 * @param {string} [props.secondaryLabel] - Secondary action text
 * @param {Function} [props.onSecondaryAction] - Secondary action click handler
 * @param {'default' | 'card' | 'dashed' | 'minimal'} [props.variant='default']
 * @param {'default' | 'primary' | 'gradient'} [props.iconVariant='default']
 * @param {'sm' | 'md' | 'lg'} [props.size='md']
 * @param {React.ReactNode} [props.children] - Custom content below description
 * @param {string} [props.className]
 */
const EmptyState = forwardRef(({
  icon,
  title,
  description,
  actionLabel,
  actionIcon,
  onAction,
  secondaryLabel,
  onSecondaryAction,
  variant = 'default',
  iconVariant = 'default',
  size = 'md',
  children,
  className,
  ...rest
}, ref) => {
  return (
    <motion.div
      ref={ref}
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        sizeClasses[size] || sizeClasses.md,
        variantClasses[variant] || variantClasses.default,
        className
      )}
      {...rest}
    >
      {/* Icon */}
      {icon && (
        <motion.div
          variants={iconFloatAnimation}
          className={clsx(
            'mb-5 flex h-16 w-16 items-center justify-center rounded-2xl',
            '[&>svg]:h-7 [&>svg]:w-7',
            iconContainerClasses[iconVariant] || iconContainerClasses.default
          )}
        >
          {icon}
        </motion.div>
      )}

      {/* Title */}
      <motion.h3
        variants={itemAnimation}
        className={clsx(
          'text-lg font-semibold tracking-tight',
          'text-slate-900 dark:text-white'
        )}
      >
        {title}
      </motion.h3>

      {/* Description */}
      {description && (
        <motion.p
          variants={itemAnimation}
          className={clsx(
            'mt-2 max-w-sm text-sm leading-relaxed',
            'text-slate-500 dark:text-slate-400'
          )}
        >
          {description}
        </motion.p>
      )}

      {/* Custom children content */}
      {children && (
        <motion.div variants={itemAnimation} className="mt-4 w-full">
          {children}
        </motion.div>
      )}

      {/* Action buttons */}
      {(actionLabel || secondaryLabel) && (
        <motion.div
          variants={itemAnimation}
          className="mt-6 flex flex-wrap items-center justify-center gap-3"
        >
          {actionLabel && (
            <motion.button
              type="button"
              onClick={onAction}
              whileTap={{ scale: 0.97 }}
              whileHover={{ scale: 1.02 }}
              className={clsx(
                'inline-flex items-center gap-2',
                'rounded-lg px-5 py-2.5',
                'text-sm font-semibold',
                'bg-indigo-600 text-white',
                'shadow-lg shadow-indigo-600/20',
                'hover:bg-indigo-700 hover:shadow-indigo-600/30',
                'active:bg-indigo-800',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                'dark:bg-indigo-500 dark:hover:bg-indigo-600',
                'dark:focus-visible:ring-offset-slate-950'
              )}
            >
              {actionIcon && (
                <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{actionIcon}</span>
              )}
              {actionLabel}
            </motion.button>
          )}

          {secondaryLabel && (
            <motion.button
              type="button"
              onClick={onSecondaryAction}
              whileTap={{ scale: 0.97 }}
              className={clsx(
                'inline-flex items-center gap-2',
                'rounded-lg px-5 py-2.5',
                'text-sm font-semibold',
                'text-slate-600 bg-white border border-slate-200',
                'shadow-sm',
                'hover:bg-slate-50 hover:border-slate-300',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                'dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
                'dark:hover:bg-slate-700 dark:hover:border-slate-600',
                'dark:focus-visible:ring-offset-slate-950'
              )}
            >
              {secondaryLabel}
            </motion.button>
          )}
        </motion.div>
      )}
    </motion.div>
  );
});

EmptyState.displayName = 'EmptyState';

export default EmptyState;
